<script setup>
import { computed } from 'vue';
import ProgressBarCurrentMax from '@/components/Gamification/ProgressBarCurrentMax.vue';
import { IconCircleOff, IconFileAnalytics, IconThumbDownFilled, IconThumbUpFilled } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import BadgePill from '@/components/BadgePill.vue';

const props = defineProps({
    team: {
        type: Object,
        required: true,
    },
});

const studentIds = computed(() => {
    return props.team.students.map((s) => s.id);
});

const positive = computed(() => {
    return props.team.students
        .map((s) => {
            return s.positive.filter((p) => studentIds.value.includes(p));
        })
        .flat();
});

const negative = computed(() => {
    return props.team.students
        .map((s) => {
            return s.negative.filter((n) => studentIds.value.includes(n));
        })
        .flat();
});

const total = computed(() => {
    return positive.value.length + negative.value.length;
});

const filteredStudents = computed(() => {
    return props.team.students
        .map((s) => {
            return {
                id: s.id,
                avatar_or_default: s.avatar_or_default,
                name_or_nickname: s.name_or_nickname,
                positive: s.positive.filter((p) => studentIds.value.includes(p)),
                negative: s.negative.filter((n) => studentIds.value.includes(n)),
            };
        })
        .filter((s) => s.positive.length || s.negative.length);
});
</script>

<template>
    <div class="mt-3">
        <div class="flex gap-2 items-center">
            <Tippy interactive>
                <IconFileAnalytics
                    class="w-6 h-6 inline-block"
                    :class="filteredStudents.length > 0 ? 'text-gray-400' : 'text-gray-300'"
                />

                <template #content>
                    <div>
                        <IconCircleOff
                            v-if="filteredStudents.length === 0"
                            class="h-10 w-10 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                        />
                        <ul v-if="filteredStudents.length > 0">
                            <li v-for="student in filteredStudents" :key="student.id" class="mt-3">
                                <div>
                                    {{ student.name_or_nickname }}
                                </div>
                                <div class="flex gap-2 items-center">
                                    <div>
                                        <img
                                            :title="student.name_or_nickname"
                                            :src="student.avatar_or_default"
                                            class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <div class="flex gap-1 items-center">
                                            <IconThumbUpFilled
                                                class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                            />
                                            <StudentsProfileStackFiltered
                                                :students="team.students"
                                                :selected="student.positive"
                                                class="inline-block h-8"
                                            />
                                        </div>
                                        <div class="flex gap-1 items-center">
                                            <IconThumbDownFilled
                                                class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                            />
                                            <StudentsProfileStackFiltered
                                                :students="team.students"
                                                :selected="student.negative"
                                                class="inline-block h-8"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </template>
            </Tippy>
            <BadgePill>
                <div class="flex gap-1 items-center">
                    <div>{{ positive.length }}</div>
                    <div class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-400" />

                    <div>vs</div>
                    <div>-{{ negative.length }}</div>
                    <div class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-400" />
                </div>
            </BadgePill>
        </div>
        <ProgressBarCurrentMax
            v-if="total > 0 && studentIds.length > 1"
            :max="total"
            :current="positive.length"
            :show-text="false"
            height="xs"
            background-color="red"
        />
        <ProgressBarCurrentMax
            v-else
            :max="1"
            :current="1"
            :show-text="false"
            height="xs"
            color="gray"
            background-color="gray"
        />
    </div>
</template>
